import { Plugin, NodeSelection } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

/**
 * When the selection is a text range (e.g. drag-select) that spans a dynamic mention,
 * ProseMirror does not add `ProseMirror-selectednode` to the atom — only full
 * `NodeSelection` does. This plugin adds a node decoration so the mention still
 * shows the same “in selection” styling as surrounding text.
 *
 * @param nodeName - Mention node type name (e.g. `dynamicMention`)
 */
export function dynamicMentionRangeSelectionPlugin({
  nodeName,
}: {
  nodeName: string;
}): Plugin {
  return new Plugin({
    props: {
      decorations(state) {
        const sel = state.selection;
        if (sel.empty) return null;

        // Whole-node selection already gets `.ProseMirror-selectednode` on the DOM.
        if (sel instanceof NodeSelection && sel.node.type.name === nodeName) {
          return null;
        }

        const a = Math.min(sel.from, sel.to);
        const b = Math.max(sel.from, sel.to);
        if (a >= b) return null;

        const decos: Decoration[] = [];

        /*
         * `nodesBetween(from, to)` can miss inline atoms when the range grazes a boundary
         * (Fragment iteration stops when `pos < to` fails before the next sibling). Scan with
         * overlap math instead, and only include nodes that truly intersect [a, b).
         */
        state.doc.descendants((node, pos) => {
          if (node.type.name !== nodeName) return;
          const end = pos + node.nodeSize;
          if (a < end && b > pos) {
            decos.push(
              Decoration.node(pos, end, {
                class: "ProseMirror-in-range-mention",
              })
            );
          }
        });

        if (decos.length === 0) return null;
        return DecorationSet.create(state.doc, decos);
      },
    },
  });
}
